const PRICE_GUIDE_FALLBACK_ENDPOINT = '/api/price-guide?limit=180&sort=price';
const HOME_IPHONE_PRICE_ENDPOINT = priceGuideEndpoint({ category: 'Apple iPhones', limit: 80 });
const HOME_IPHONE_PRICE_PAGE = '/prisguide-iphone-produkter/';
const HOME_DEAL_MODULE = 'home-weekly-deals';
const HOME_TRACKING_QUERY = 'utm_source=weloveapple&utm_medium=homepage&utm_campaign=weekly_deals';
const ARTICLE_DEAL_MODULE = 'article-price-sidebar';
const ARTICLE_TRACKING_QUERY = 'utm_source=weloveapple&utm_medium=article&utm_campaign=price_sidebar';

const PRODUCT_FAMILIES = [
  { key: 'iphone', label: 'iPhone', minPrice: 1000, test: /\biphone\b/i },
  { key: 'ipad', label: 'iPad', minPrice: 800, test: /\bipad\b/i },
  { key: 'airpods', label: 'AirPods', minPrice: 250, test: /\bairpods\b/i },
  { key: 'watch', label: 'Apple Watch', minPrice: 500, test: /\bapple watch\b/i },
  { key: 'mac', label: 'Mac', minPrice: 2500, test: /\b(macbook|imac|mac mini|mac studio|mac pro)\b/i },
  { key: 'accessories', label: 'Tilbehør', minPrice: 150, test: /\b(airtag|apple tv|homepod|magsafe|usb-c|oplader|kabel|keyboard|magic mouse|trackpad)\b/i }
];

const DEAL_CATEGORY_REQUESTS = [
  { category: 'Apple iPhones', family: 'iphone', limit: 80 },
  { category: 'Apple iPads', family: 'ipad', limit: 80 },
  { category: 'Apple AirPods', family: 'airpods', limit: 30 },
  { category: 'Apple Watch', family: 'watch', limit: 30 },
  { category: 'Apple MacBooks', family: 'mac', limit: 40 },
  { category: 'Airtag og SmartTag', family: 'accessories', limit: 15 },
  { category: 'Apple TV', family: 'accessories', limit: 15 },
  { category: 'Opladere til mobil', family: 'accessories', limit: 25 }
];

const ACCESSORY_TEXT_RE = /\b(adapter|beskyttelsescover|cover|case|folio|kabel|keyboard|lightning|magsafe|mouse|mus|oplader|power adapter|skærmbeskyttelse|smart tastatur|tastatur|trackpad|usb-c|usb-a)\b/i;
const LOW_SIGNAL_ACCESSORY_RE = /\b(beskyttelsescover|mobilcovers?|cover|case|folio|keyboard|mouse|mus|skærmbeskyttelse|smart tastatur|tastatur|trackpad|rem)\b/i;

function cleanText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function safeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function cleanUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';

  try {
    const url = new URL(raw, window.location.origin);
    if (!['http:', 'https:'].includes(url.protocol)) return '';
    return url.toString();
  } catch {
    return '';
  }
}

function priceGuideEndpoint({ category = '', limit = 40 } = {}) {
  const params = new URLSearchParams({
    limit: String(limit),
    sort: 'price'
  });
  if (category) params.set('category', category);
  return `/api/price-guide?${params.toString()}`;
}

async function fetchPriceGuide(endpoint) {
  const response = await fetch(endpoint, {
    headers: { accept: 'application/json' }
  });
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data?.products) ? data.products : [];
}

function slugify(value) {
  return cleanText(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'oe')
    .replace(/å/g, 'aa')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 96) || 'apple-produkt';
}

function productUrl(product, rank, trackingQuery = HOME_TRACKING_QUERY) {
  const id = encodeURIComponent(String(product.id || product.catalogProductId || ''));
  const slug = slugify(product.name);
  const path = id ? `/prisguide/produkt/${id}/${slug}/` : '/prisguide/';
  return `${path}?${trackingQuery}&utm_content=deal_${rank}`;
}

function familyFor(product) {
  const haystack = productHaystack(product);
  const accessories = PRODUCT_FAMILIES.find((family) => family.key === 'accessories');
  if (ACCESSORY_TEXT_RE.test(haystack)) return accessories;

  if (product._dealFamily) {
    const explicitFamily = PRODUCT_FAMILIES.find((family) => family.key === product._dealFamily);
    if (explicitFamily) return explicitFamily;
  }

  return PRODUCT_FAMILIES.find((family) => family.key !== 'accessories' && family.test.test(haystack)) || null;
}

function productHaystack(product) {
  return cleanText(
    [
      product.category,
      product.model,
      product.name,
      product.keywords,
      product.bestOffer?.productUrl,
      product.bestOffer?.outboundUrl
    ].filter(Boolean).join(' ')
  );
}

function productScore(product) {
  const name = cleanText(product.name);
  const price = safeNumber(product.minPrice);
  const offerCount = safeNumber(product.offerCount);
  const retailerCount = safeNumber(product.retailerCount);
  const family = familyFor(product);
  const haystack = productHaystack(product);
  const isLikelyNoise = /\b(samsung|galaxy|pixel|motorola|xiaomi|nintendo)\b/i.test(haystack);
  const isSpeculative = /\bmacbook neo\b/i.test(name);
  const isTooCheapForFamily = family?.minPrice ? price < family.minPrice : price < 150;
  const isLowSignalAccessory = LOW_SIGNAL_ACCESSORY_RE.test(haystack);

  if (!product.id || !name || !price || isLikelyNoise || isSpeculative || isTooCheapForFamily || isLowSignalAccessory) return -999;

  let score = 0;
  if (product.imageUrl) score += 25;
  if (price >= 500) score += 20;
  if (price >= 1000) score += 12;
  if (offerCount > 1) score += 8;
  if (retailerCount > 1) score += 8;
  if (product.bestOffer?.dealSignal === 'drop') score += 35;
  if (product.bestOffer?.dealSignal === 'lowest') score += 25;
  if (/iphone|ipad|macbook|apple watch|airpods/i.test(name)) score += 16;

  return score;
}

function selectDeals(products) {
  const scored = products
    .map((product) => ({ product, family: familyFor(product), score: productScore(product) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  const selected = [];
  const usedFamilies = new Set();
  const usedProducts = new Set();

  PRODUCT_FAMILIES.forEach((family) => {
    const match = scored.find((entry) => entry.family?.key === family.key && !usedProducts.has(entry.product.id));
    if (!match || selected.length >= 3) return;
    selected.push(match);
    usedFamilies.add(family.key);
    usedProducts.add(match.product.id);
  });

  scored.forEach((entry) => {
    if (selected.length >= 3 || usedProducts.has(entry.product.id)) return;
    if (entry.family?.key && usedFamilies.has(entry.family.key)) return;
    selected.push(entry);
    if (entry.family?.key) usedFamilies.add(entry.family.key);
    usedProducts.add(entry.product.id);
  });

  return selected.slice(0, 3).map((entry) => entry.product);
}

function priceLabel(value) {
  const price = safeNumber(value);
  if (!price) return '';
  return `${Math.round(price).toLocaleString('da-DK')} DKK`;
}

function createCompactPriceRow(product, index, placement = 'home_live_price') {
  const price = priceLabel(product?.minPrice);
  const name = cleanText(product?.name);
  if (!product?.id || !name || !price) return null;
  const link = document.createElement('a');
  link.className = 'wla-home-v3__price-row';
  link.href = productUrl(product, index, `${HOME_TRACKING_QUERY}&utm_content=${placement}`);
  link.dataset.wlaInternalLink = '1';
  link.dataset.wlaInternalModule = 'home-price-guide';
  link.dataset.wlaInternalPlacement = placement;
  link.dataset.wlaInternalRank = String(index + 1);
  const title = document.createElement('span');
  title.textContent = name;
  const amount = document.createElement('strong');
  amount.textContent = price.replace(' DKK', ' kr.');
  link.append(title, amount);
  return link;
}

function selectLowestIphone(products) {
  return products
    .filter((product) => {
      const price = safeNumber(product.minPrice);
      const name = cleanText(product.name);
      return product.id && price >= 1000 && /\biphone\b/i.test(name);
    })
    .sort((a, b) => safeNumber(a.minPrice) - safeNumber(b.minPrice))[0] || null;
}

async function fetchLowestIphoneFromPricePage() {
  const response = await fetch(HOME_IPHONE_PRICE_PAGE, {
    headers: { accept: 'text/html' }
  });
  if (!response.ok) return null;

  const html = await response.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const cards = Array.from(doc.querySelectorAll('[data-price-product-card]'));
  const products = cards
    .map((card) => ({
      id: card.getAttribute('data-price-product-id'),
      name: cleanText(card.getAttribute('data-price-name')),
      minPrice: safeNumber(card.getAttribute('data-price-price')),
      offerCount: safeNumber(card.textContent.match(/(\d+)\s+priser?/)?.[1]),
      retailerCount: safeNumber(card.textContent.match(/fra\s+(\d+)\s+forhandlere?/)?.[1])
    }))
    .filter((product) => product.id && product.name && product.minPrice && /\biphone\b/i.test(product.name))
    .sort((a, b) => a.minPrice - b.minPrice);

  return products[0] || null;
}

async function initHomePriceGuideCard() {
  const card = document.querySelector('[data-wla-home-price-guide-card]');
  if (!card) return;

  const title = card.querySelector('[data-wla-home-price-guide-title]');
  const meta = card.querySelector('[data-wla-home-price-guide-meta]');
  if (!title) return;

  try {
    const products = await fetchPriceGuide(HOME_IPHONE_PRICE_ENDPOINT);
    const product = selectLowestIphone(products) || await fetchLowestIphoneFromPricePage();
    const price = priceLabel(product?.minPrice);
    if (!product || !price) return;

    const offerCount = safeNumber(product.offerCount);
    const retailerCount = safeNumber(product.retailerCount);
    title.textContent = `iPhone fra ${price}`;

    if (meta) {
      const offers = offerCount
        ? `${offerCount} ${offerCount === 1 ? 'pris' : 'priser'}`
        : 'Aktuelle priser';
      const retailers = retailerCount
        ? `${retailerCount} ${retailerCount === 1 ? 'forhandler' : 'forhandlere'}`
        : 'prisguiden';
      meta.textContent = `${offers} fra ${retailers} på originale Apple-produkter.`;
    }
    const list = card.querySelector('[data-wla-home-price-guide-list]');
    if (list) {
      const rows = products
        .filter((entry) => productScore(entry) > 0)
        .sort((a, b) => safeNumber(a.minPrice) - safeNumber(b.minPrice))
        .slice(0, 5)
        .map((entry, index) => createCompactPriceRow(entry, index))
        .filter(Boolean);
      if (rows.length) list.replaceChildren(...rows);
    }
    const updated = card.querySelector('[data-wla-home-price-guide-updated]');
    if (updated) updated.textContent = 'Opdateret nu';
  } catch {}
}

async function loadDealProducts() {
  const categoryResults = await Promise.allSettled(
    DEAL_CATEGORY_REQUESTS.map(async (request) => {
      const products = await fetchPriceGuide(priceGuideEndpoint(request));
      return products.map((product) => ({ ...product, _dealFamily: request.family }));
    })
  );
  const products = categoryResults.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));
  if (products.length) return products;
  return fetchPriceGuide(PRICE_GUIDE_FALLBACK_ENDPOINT);
}

function decorateTrackedLink(
  link,
  product,
  rank,
  { trackingModule = HOME_DEAL_MODULE, trackingPlacement = 'home_weekly_deals' } = {}
) {
  link.dataset.wlaInternalLink = '1';
  link.dataset.wlaInternalModule = trackingModule;
  link.dataset.wlaInternalPlacement = trackingPlacement;
  link.dataset.wlaInternalRank = String(rank);
  link.dataset.wlaInternalAnchor = cleanText(product.name);
}

function createDealCard(product, index, options = {}) {
  const baseClass = options.baseClass || 'wla-home-deal-card';
  const trackingModule = options.trackingModule || HOME_DEAL_MODULE;
  const trackingPlacement = options.trackingPlacement || 'home_weekly_deals';
  const trackingQuery = options.trackingQuery || HOME_TRACKING_QUERY;
  const rank = index + 1;
  const title = cleanText(product.name);
  const href = productUrl(product, rank, trackingQuery);
  const imageUrl = cleanUrl(product.imageUrl);
  const family = familyFor(product);
  const retailerName = cleanText(product.bestOffer?.retailerName);
  const condition = cleanText(product.bestOffer?.conditionLabel || product.bestOffer?.condition);
  const price = cleanText(product.priceLabel || (product.minPrice ? `${Math.round(Number(product.minPrice)).toLocaleString('da-DK')} DKK` : ''));

  if (!title || !href || !price) return null;

  const article = document.createElement('article');
  article.className = baseClass;

  const imageLink = document.createElement('a');
  imageLink.className = imageUrl ? `${baseClass}__image` : `${baseClass}__image is-empty`;
  imageLink.href = href;
  imageLink.setAttribute('aria-label', title);
  decorateTrackedLink(imageLink, product, rank, { trackingModule, trackingPlacement });

  if (imageUrl) {
    const image = document.createElement('img');
    image.src = imageUrl;
    image.alt = '';
    image.loading = 'lazy';
    image.decoding = 'async';
    imageLink.append(image);
  } else {
    const placeholder = document.createElement('span');
    placeholder.textContent = family?.label || 'Prisguide';
    imageLink.append(placeholder);
  }

  const body = document.createElement('div');
  body.className = `${baseClass}__body`;

  const eyebrow = document.createElement('span');
  eyebrow.className = `${baseClass}__eyebrow`;
  eyebrow.textContent = family?.label || 'Prisguide';

  const heading = document.createElement('h3');
  const headingLink = document.createElement('a');
  headingLink.href = href;
  headingLink.textContent = title;
  decorateTrackedLink(headingLink, product, rank, { trackingModule, trackingPlacement });
  heading.append(headingLink);

  const meta = document.createElement('p');
  const offerText = `${safeNumber(product.offerCount)} ${safeNumber(product.offerCount) === 1 ? 'pris' : 'priser'}`;
  const retailerText = `${safeNumber(product.retailerCount)} ${safeNumber(product.retailerCount) === 1 ? 'forhandler' : 'forhandlere'}`;
  meta.textContent = [offerText, retailerText, condition || retailerName].filter(Boolean).join(' · ');

  const priceLine = document.createElement('div');
  priceLine.className = `${baseClass}__price`;
  priceLine.innerHTML = `<span>Pris fra</span><strong>${price}</strong>`;

  body.append(eyebrow, heading, meta, priceLine);
  article.append(imageLink, body);
  return article;
}

export async function initHomeDeals() {
  initHomePriceGuideCard();

  const root = document.querySelector('[data-wla-home-deals]');
  const list = root?.querySelector('[data-wla-home-deals-list]');
  if (!root || !list) return;

  try {
    const products = await loadDealProducts();
    const popularList = document.querySelector('[data-wla-home-popular-prices]');
    if (popularList) {
      const popularRows = selectDeals(products)
        .concat(products.filter((product) => productScore(product) > 0))
        .filter((product, index, all) => all.findIndex((entry) => entry.id === product.id) === index)
        .slice(0, 5)
        .map((product, index) => createCompactPriceRow(product, index, 'home_popular_prices'))
        .filter(Boolean);
      if (popularRows.length) popularList.replaceChildren(...popularRows);
    }
    const cards = selectDeals(products).map(createDealCard).filter(Boolean);
    if (!cards.length) return;

    list.replaceChildren(...cards);
    root.hidden = false;
  } catch {}
}

export async function initArticlePriceSidebar() {
  const sidebars = Array.from(document.querySelectorAll('[data-wla-article-price-sidebar]'));
  if (!sidebars.length) return;

  try {
    const products = await loadDealProducts();
    const deals = selectDeals(products);
    if (!deals.length) return;

    sidebars.forEach((root) => {
      const list = root.querySelector('[data-wla-article-price-sidebar-list]');
      if (!list) return;

      const cards = deals
        .map((product, index) => createDealCard(product, index, {
          baseClass: 'wla-article-price-card',
          trackingModule: ARTICLE_DEAL_MODULE,
          trackingPlacement: 'article_price_sidebar',
          trackingQuery: ARTICLE_TRACKING_QUERY
        }))
        .filter(Boolean);

      if (!cards.length) return;
      list.replaceChildren(...cards);
      root.dataset.wlaArticlePriceSidebarLoaded = 'true';
    });
  } catch {}
}
